import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useEditor, useEditorState, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import DragHandle from '@tiptap/extension-drag-handle-react';
import {
  Box, Flex, Text, useColorModeValue, useToast, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, Input, FormControl, FormLabel, HStack, IconButton, Tooltip, Icon,
} from '@chakra-ui/react';
import {
  LuBold, LuItalic, LuUnderline, LuStrikethrough, LuLink, LuHighlighter, LuGripVertical,
} from 'react-icons/lu';
import { uploadWikiImage } from '../../services/api';
import { buildExtensions, normalizeContent } from './extensions';
import EditorToolbar from './EditorToolbar';
import BlockPalette, { insertWikiBlock, WIKI_BLOCK_MIME } from './BlockPalette';
import MarkdownImportModal from './MarkdownImportModal';
import { looksLikeMarkdown, markdownToHtml, docToPlainText, isRawMarkdownDoc } from './markdown';
import './wiki-editor.css';

export const WIKI_ATTACHMENT_MIME = 'application/x-wiki-attachment';

const NOS_SEM_TEXTO = ['image', 'youtube', 'table', 'horizontalRule', 'htmlBlock', 'pageBreak'];

export const docHasContent = (doc) => {
  if (!doc) return false;
  if (doc.type === 'text') return Boolean(doc.text && doc.text.trim());
  if (NOS_SEM_TEXTO.includes(doc.type)) return true;
  return (doc.content || []).some(docHasContent);
};

const insertWikiAttachment = (editor, att, pos = null) => {
  if (!editor || !att?.url) return;
  const chain = pos !== null && pos !== undefined ? editor.chain().focus(pos) : editor.chain().focus();
  const isImage = att.type === 'image' || /\.(png|jpe?g|gif|webp)(\?|$)/i.test(att.url);
  if (isImage) {
    chain.insertContent({ type: 'image', attrs: { src: att.url, alt: att.name || null } }).run();
  } else {
    chain.insertContent({
      type: 'paragraph',
      content: [{
        type: 'text',
        text: `📎 ${att.name || 'Anexo'}`,
        marks: [{ type: 'link', attrs: { href: att.url, target: '_blank', rel: 'noopener noreferrer' } }],
      }],
    }).run();
  }
};

const UrlModal = ({ isOpen, onClose, title, label, placeholder, initialValue, onConfirm, onRemove, removeLabel }) => {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (isOpen) setValue(initialValue || '');
  }, [isOpen, initialValue]);

  const confirm = () => {
    if (value.trim()) onConfirm(value.trim());
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel fontSize="sm">{label}</FormLabel>
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } }}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          {onRemove && (
            <Button size="sm" variant="outline" colorScheme="red" mr="auto" onClick={() => { onRemove(); onClose(); }}>
              {removeLabel}
            </Button>
          )}
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancelar</Button>
          <Button size="sm" colorScheme="orange" onClick={confirm}>Aplicar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const BubbleButton = ({ label, icon, onClick, isActive }) => (
  <Tooltip label={label} fontSize="xs" openDelay={300}>
    <IconButton
      aria-label={label}
      icon={icon}
      size="xs"
      variant={isActive ? 'solid' : 'ghost'}
      colorScheme={isActive ? 'orange' : 'gray'}
      onClick={onClick}
    />
  </Tooltip>
);

const WikiEditor = ({ value, onChange, placeholder, minHeight = '400px' }) => {
  const toast = useToast();
  const fileInputRef = useRef();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [youtubeModalOpen, setYoutubeModalOpen] = useState(false);
  const [markdownModalOpen, setMarkdownModalOpen] = useState(false);
  const [markdownInicial, setMarkdownInicial] = useState('');

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const toolbarBg = useColorModeValue('gray.50', 'gray.700');
  const editorBg = useColorModeValue('white', 'gray.800');
  const bubbleBg = useColorModeValue('white', 'gray.700');

  const uploadImageFile = useCallback(async (editorInstance, file, pos = null) => {
    if (!file || !file.type.startsWith('image/')) return;
    toast({ title: 'Enviando imagem...', status: 'info', duration: 1500, position: 'top-right' });
    const url = await uploadWikiImage(file);
    if (!url) {
      toast({ title: 'Erro ao enviar imagem', status: 'error', position: 'top-right' });
      return;
    }
    const chain = editorInstance.chain().focus();
    if (pos !== null) chain.insertContentAt(pos, { type: 'image', attrs: { src: url } }).run();
    else chain.insertContent({ type: 'image', attrs: { src: url } }).run();
  }, [toast]);

  const uploadRef = useRef(uploadImageFile);
  uploadRef.current = uploadImageFile;

  const editor = useEditor({
    extensions: buildExtensions({ placeholder }),
    content: normalizeContent(value) || '',
    editorProps: {
      attributes: { class: 'wiki-editor-content' },
      handleDrop: (view, event, slice, moved) => {
        if (moved) return false;
        const attData = event.dataTransfer?.getData(WIKI_ATTACHMENT_MIME);
        if (attData) {
          event.preventDefault();
          const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
          try {
            insertWikiAttachment(editorRef.current, JSON.parse(attData), coords?.pos ?? null);
          } catch {}
          return true;
        }
        const blockType = event.dataTransfer?.getData(WIKI_BLOCK_MIME);
        if (blockType) {
          event.preventDefault();
          const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
          insertWikiBlock(editorRef.current, blockType, coords?.pos ?? null);
          return true;
        }
        const files = Array.from(event.dataTransfer?.files || []).filter((f) => f.type.startsWith('image/'));
        if (!files.length) return false;
        event.preventDefault();
        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
        files.forEach((f) => uploadRef.current(editorRef.current, f, coords?.pos ?? null));
        return true;
      },
      handlePaste: (view, event) => {
        const files = Array.from(event.clipboardData?.files || []).filter((f) => f.type.startsWith('image/'));
        if (files.length) {
          event.preventDefault();
          files.forEach((f) => uploadRef.current(editorRef.current, f));
          return true;
        }

        // Markdown colado (ex: saída de LLM) é formatado automaticamente.
        // Só entra aqui quando não veio HTML na área de transferência.
        const html = event.clipboardData?.getData('text/html');
        const texto = event.clipboardData?.getData('text/plain');
        if (!html && looksLikeMarkdown(texto)) {
          event.preventDefault();
          editorRef.current?.chain().focus().insertContent(markdownToHtml(texto)).run();
          toast({
            title: 'Markdown formatado',
            description: 'Ctrl+Z desfaz e cola como texto puro.',
            status: 'info', duration: 4000, position: 'top-right', isClosable: true,
          });
          return true;
        }

        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChangeRef.current?.({ json: ed.getJSON(), text: ed.getText() });
    },
  });

  const editorRef = useRef(null);
  editorRef.current = editor;

  useEffect(() => {
    if (!editor) return;
    editor.wikiRequestImage = () => fileInputRef.current?.click();
    editor.wikiRequestYoutube = () => setYoutubeModalOpen(true);
    editor.wikiRequestLink = () => setLinkModalOpen(true);
    editor.wikiRequestMarkdown = () => {
      // Página antiga salva com markdown cru: já abre com o conteúdo pronto pra converter.
      const doc = editor.getJSON();
      setMarkdownInicial(isRawMarkdownDoc(doc) ? docToPlainText(doc) : '');
      setMarkdownModalOpen(true);
    };
  }, [editor]);

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file && editor) uploadImageFile(editor, file);
  };

  const bubbleState = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      if (!ed) return null;
      return {
        bold: ed.isActive('bold'),
        italic: ed.isActive('italic'),
        underline: ed.isActive('underline'),
        strike: ed.isActive('strike'),
        highlight: ed.isActive('highlight'),
        link: ed.isActive('link'),
        linkHref: ed.getAttributes('link').href || '',
        words: ed.storage.characterCount?.words() ?? 0,
        chars: ed.storage.characterCount?.characters() ?? 0,
      };
    },
  });

  const bubbleShouldShow = useCallback(({ editor: ed, state }) =>
    !state.selection.empty &&
    ed.isEditable &&
    !ed.isActive('image') &&
    !ed.isActive('htmlBlock') &&
    !ed.isActive('codeBlock'),
  []);

  if (!editor || !bubbleState) return null;

  const { words, chars } = bubbleState;

  return (
    <Box className="wiki-editor-shell" border="1px solid" borderColor={borderColor} borderRadius="lg" overflow="hidden" bg={editorBg} transition="border-color 0.2s, box-shadow 0.2s">
      <Box borderBottom="1px solid" borderColor={borderColor} bg={toolbarBg}>
        <EditorToolbar editor={editor} />
      </Box>

      <Box borderBottom="1px solid" borderColor={borderColor}>
        <BlockPalette editor={editor} />
      </Box>

      <Box
        position="relative"
        minH={{ base: '300px', md: minHeight }}
        maxH={{ base: '62vh', md: '68vh' }}
        overflowY="auto"
        className="wiki-editor-wrapper"
        onClick={(e) => { if (e.target === e.currentTarget) editor.chain().focus('end').run(); }}
      >
        <DragHandle editor={editor}>
          <Flex className="wiki-drag-handle" align="center" justify="center">
            <Icon as={LuGripVertical} boxSize={4} color="gray.400" />
          </Flex>
        </DragHandle>

        <BubbleMenu editor={editor} shouldShow={bubbleShouldShow}>
          <HStack spacing={0.5} bg={bubbleBg} borderRadius="md" boxShadow="lg" border="1px solid" borderColor={borderColor} p={1}>
            <BubbleButton label="Negrito" icon={<LuBold />} onClick={() => editor.chain().focus().toggleBold().run()} isActive={bubbleState.bold} />
            <BubbleButton label="Itálico" icon={<LuItalic />} onClick={() => editor.chain().focus().toggleItalic().run()} isActive={bubbleState.italic} />
            <BubbleButton label="Sublinhado" icon={<LuUnderline />} onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={bubbleState.underline} />
            <BubbleButton label="Tachado" icon={<LuStrikethrough />} onClick={() => editor.chain().focus().toggleStrike().run()} isActive={bubbleState.strike} />
            <BubbleButton label="Destacar" icon={<LuHighlighter />} onClick={() => editor.chain().focus().toggleHighlight({ color: '#FEFCBF' }).run()} isActive={bubbleState.highlight} />
            <BubbleButton label="Link" icon={<LuLink />} onClick={() => setLinkModalOpen(true)} isActive={bubbleState.link} />
          </HStack>
        </BubbleMenu>

        <EditorContent editor={editor} />
      </Box>

      <Flex justify="flex-end" px={3} py={1} borderTop="1px solid" borderColor={borderColor} bg={toolbarBg}>
        <Text fontSize="xs" color="gray.500">
          {words} {words === 1 ? 'palavra' : 'palavras'} · {chars} caracteres
        </Text>
      </Flex>

      <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelected} />

      <UrlModal
        isOpen={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        title="Inserir link"
        label="URL"
        placeholder="https://exemplo.com"
        initialValue={bubbleState.linkHref}
        onConfirm={(url) => editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()}
        onRemove={bubbleState.link ? () => editor.chain().focus().extendMarkRange('link').unsetLink().run() : null}
        removeLabel="Remover link"
      />

      <UrlModal
        isOpen={youtubeModalOpen}
        onClose={() => setYoutubeModalOpen(false)}
        title="Incorporar vídeo do YouTube"
        label="URL do vídeo"
        placeholder="https://www.youtube.com/watch?v=..."
        onConfirm={(url) => editor.chain().focus().setYoutubeVideo({ src: url }).run()}
      />

      <MarkdownImportModal
        isOpen={markdownModalOpen}
        onClose={() => setMarkdownModalOpen(false)}
        initialValue={markdownInicial}
        onInsert={(html) => editor.chain().focus().insertContent(html).run()}
        onReplace={(html) => editor.chain().focus().setContent(html).run()}
      />
    </Box>
  );
};

export default WikiEditor;
